import { useEffect, useRef, useState } from 'react'
import EmblaCarouselCore from 'embla-carousel'
import './styles/index.scss'
import useBreakpoint from 'utils/useBreakpoint'
import { SLIDES_TO_SCROLL_CONFIG, BASE_SLIDES_TO_SHOW_CONFIG, TENANT_SLIDES_TO_SHOW_OVERRIDES, CONTROL_POSITION, SLIDE_TYPES } from 'constants/slider'
import { useSelector } from 'react-redux'

import Button from 'components/Common/Button'
import { BUTTON_VARIANT } from 'utils/constants'

// gameSliderOptions (tenant-wise configuration):
// ------------------------------------------------
// gameSliderDragFree: true/false
//    Enables free dragging behavior for the slider.
// gameSliderDragFreeCardWidth: string/number
//    If provided, automatically adjusts how many cards are visible per row
//    based on the card width.
// gameSliderSlideByVisibleMinusOne: true/false
//    Controls slide movement. When enabled, carousel slides by (visibleCards - 1),
//    When Disabled, carousel slides by 1.
//    only when provided, gameSliderDragFreeCardWidth.
// gameSliderCardGap: string/number
//    Defines the gap/spacing between the game cards.

/**
 * Slider component using Embla Carousel for horizontal and vertical sliding.
 *
 * @component
 * @param {Object} props - Slider props.
 * @param {boolean} [props.showNavigation=true] - Show navigation arrows.
 * @param {Array} [props.slides=[]] - Array of slide data.
 * @param {Function} [props.sliderCard=() => null] - Function to render each slide.
 * @param {React.ReactNode} [props.nextArrow] - Custom next arrow element.
 * @param {React.ReactNode} [props.prevArrow] - Custom previous arrow element.
 * @param {string} [props.type='gameCard'] - Type/variant of slider.
 * @param {'top'|'bottom'|'left_right'} [props.controlPosition='top'] - Position of navigation controls.
 * @param {boolean} [props.isVertical=false] - Enable vertical sliding.
 * @param {boolean} [props.autoPlay=false] - Enable autoplay.
 * @param {number} [props.autoPlayInterval=3000] - Autoplay interval in ms.
 * @param {boolean} [props.loop=true] - Enable loop mode
 * @returns {JSX.Element} Slider carousel component.
 *
 * @example
 * <Slider
 *   slides={data}
 *   sliderCard={({ index, game }) => <Card game={game} />}
 *   isVertical={true}
 *   autoPlay={true}
 *   autoPlayInterval={4000}
 * />
 */
const SliderDefault = ({
  showNavigation = true,
  slides = [],
  sliderCard = () => null,
  nextArrow,
  prevArrow,
  type = SLIDE_TYPES.GAME_CARD,
  controlPosition = CONTROL_POSITION.TOP,
  isVertical = false,
  autoPlay = false,
  autoPlayInterval = 3000,
  showHeader = true,
  headerIcon = () => null,
  headerTitle = '',
  onClickViewAll = () => { },
  showViewAllButton = false,
  viewAllButtonText = '',
  loop = true,
  dragAllowed = true,
  cardProps = {},
  navigationFormatIsSlider
}) => {
  const uiConfig = useSelector((state) => state?.uiConfig?.general)
  const { gameCardVariant = 'v1' } = uiConfig || {}
  const gameSliderDragFree = uiConfig?.gameSliderOptions?.gameSliderDragFree || false
  const gameSliderDragFreeCardWidth = uiConfig?.gameSliderOptions?.gameSliderDragFreeCardWidth
  const gameSliderSlideByVisibleMinusOne = uiConfig?.gameSliderOptions?.gameSliderSlideByVisibleMinusOne || false
  const gameSliderCardGap = uiConfig?.gameSliderOptions?.gameSliderCardGap
  const navigationFormatSlider = navigationFormatIsSlider || uiConfig?.gameSliderOptions?.navigationFormatIsSlider
  const slidesToShowOverride = uiConfig?.slidesToShowOverride
  const emblaRef = useRef(null)
  const emblaApi = useRef(null)
  const breakPoint = useBreakpoint()
  const [slideWidth, setSlideWidth] = useState(0)
  const [slidesToScroll, setSlidesToScroll] = useState(1)
  // eslint-disable-next-line no-unused-vars
  const [slidesToShow, setSlidesToShow] = useState(2)
  const [prevDisabled, setPrevDisabled] = useState(false)
  const [nextDisabled, setNextDisabled] = useState(false)
  const [scrollSnaps, setScrollSnaps] = useState(0)
  const [selectedIndex, setSelectedIndex] = useState(0)

  const setNavigationControlsState = () => {
    if (emblaApi.current) {
      setPrevDisabled(!emblaApi.current.canScrollPrev())
      setNextDisabled(!emblaApi.current.canScrollNext())
    }
  }

  const scrollPrev = () => {
    if (emblaApi.current) {
      emblaApi.current.scrollPrev()
      setNavigationControlsState()
    }
  }

  const scrollNext = () => {
    if (emblaApi.current) {
      emblaApi.current.scrollNext()
      setNavigationControlsState()
    }
  }

  useEffect(() => {
    if (!emblaRef.current) return

    if (emblaApi.current) {
      emblaApi.current.destroy()
    }

    let options = {
      loop,
      slidesToScroll,
      align: 'start',
      watchDrag: dragAllowed
    }

    // Add drag-free options only for game cards when enabled
    if (gameSliderDragFree && type === SLIDE_TYPES.GAME_CARD) {
      options.dragFree = true
      options.containScroll = 'trimSnaps'
    }

    if (isVertical) {
      options = {
        axis: 'y',
        align: 'start',
        loop,
        watchDrag: dragAllowed
      }

      // Add drag-free options for vertical game cards when enabled
      if (gameSliderDragFree && type === SLIDE_TYPES.GAME_CARD) {
        options.dragFree = true
        options.containScroll = 'trimSnaps'
      }
    }

    emblaApi.current = EmblaCarouselCore(emblaRef.current, options)

    // Setup dots data and keep selection in sync
    if (emblaApi.current) {
      const updateDotsState = () => {
        setSelectedIndex(emblaApi.current.selectedScrollSnap())
        setNavigationControlsState()
      }
      setScrollSnaps(emblaApi.current.scrollSnapList().length)
      setSelectedIndex(emblaApi.current.selectedScrollSnap())
      setNavigationControlsState()
      emblaApi.current.on('select', updateDotsState)
      emblaApi.current.on('reInit', () => {
        setScrollSnaps(emblaApi.current.scrollSnapList().length)
        updateDotsState()
      })
    }
  }, [isVertical, loop, slidesToScroll, gameSliderDragFree, dragAllowed])

  useEffect(() => {
    if (breakPoint) {
      // Skip responsive logic if ignore responsive width is enabled
      if (!!gameSliderDragFreeCardWidth && type === SLIDE_TYPES.GAME_CARD) {
        // Let cards flow naturally with their SCSS-defined widths
        setSlideWidth('auto')
        setSlidesToShow(999) // Large number to allow natural flow

        // Still handle slide to scroll logic even when ignoring responsive width
        if (gameSliderSlideByVisibleMinusOne) {
          // Calculate based on viewport width and configured card width
          const cardWidth = gameSliderDragFreeCardWidth
            ? parseInt(gameSliderDragFreeCardWidth.replace('px', ''))
            : 180 // Default to 180px
          const estimatedCardsVisible = Math.floor(window.innerWidth / cardWidth)
          setSlidesToScroll(Math.max(1, estimatedCardsVisible - 1) || 1)
        } else {
          setSlidesToScroll(1)
        }
        return
      }

      let version = gameCardVariant
      if (type !== SLIDE_TYPES.GAME_CARD) {
        version = type
      }
      const overrideBreakpoints = slidesToShowOverride && TENANT_SLIDES_TO_SHOW_OVERRIDES[slidesToShowOverride]?.[version]
      const effectiveSlidesToShowConfig = overrideBreakpoints
        ? { ...BASE_SLIDES_TO_SHOW_CONFIG[version], ...overrideBreakpoints }
        : BASE_SLIDES_TO_SHOW_CONFIG[version]
      const updatedSlidesToShow = effectiveSlidesToShowConfig?.[breakPoint] || 2

      // When an override is active, scroll by the same number of cards being shown
      // so the slider advances a full "page" worth of visible cards.
      // Otherwise fall back to the default per-variant scroll config.
      const updatedSlidesToScroll = overrideBreakpoints
        ? Math.floor(updatedSlidesToShow)
        : SLIDES_TO_SCROLL_CONFIG?.[version]?.[breakPoint] || 1
      setSlidesToScroll(updatedSlidesToScroll)

      // Calculate slide width accounting for gap if present
      if (gameSliderCardGap && type === SLIDE_TYPES.GAME_CARD) {
        const gapValue = `${gameSliderCardGap}px`
        // Number of gaps = number of cards - 1
        const numberOfGaps = updatedSlidesToShow - 1
        setSlideWidth(`calc((100% - ${numberOfGaps} * ${gapValue}) / ${updatedSlidesToShow})`)
      } else {
        setSlideWidth(`${100 / updatedSlidesToShow}%`)
      }
      setSlidesToShow(updatedSlidesToShow)
    }
  }, [breakPoint, gameSliderSlideByVisibleMinusOne, gameSliderCardGap, type, gameCardVariant, slidesToShowOverride])

  // Autoplay effect
  useEffect(() => {
    if (!autoPlay || !emblaApi.current) return

    const interval = setInterval(() => {
      if (emblaApi.current) {
        emblaApi.current.scrollNext()
      }
    }, autoPlayInterval)
    return () => clearInterval(interval)
  }, [autoPlay, slides.length, isVertical])

  useEffect(() => {
    if (controlPosition === CONTROL_POSITION.LEFT_RIGHT && !loop) {
      setPrevDisabled(true)
    }
  }, [controlPosition, loop])

  const renderNavigation = () => {
    if (navigationFormatSlider) {
      return (
        <div className='embla__progress-bar'>
          {scrollSnaps > 0 && (
            <div
              className='embla__progress-track'
              style={{
                width: `${(1 / scrollSnaps) * 100}%`,
                transform: `translateX(${selectedIndex * 100}%)`
              }}
            />
          )}
        </div>
      )
    }
    return (
      <div className='embla__dots'>
        {scrollSnaps <= 10 && Array.from({ length: scrollSnaps }).map((_, i) => (
          <span key={i} className={`embla__dot ${i === selectedIndex ? 'is-selected' : ''}`} />
        ))}
      </div>
    )
  }

  return (
    <div className={`embla__wrapper ${isVertical ? 'embla__wrapper--vertical' : ''} ${controlPosition === CONTROL_POSITION.LEFT_RIGHT ? 'embla__wrapper--left-right-controls' : ''}`}>
      {
        showNavigation && controlPosition === CONTROL_POSITION.LEFT_RIGHT && (
          <div className='embla__controls_left_right'>
            <button
              disabled={prevDisabled}
              onClick={scrollPrev}
              className='embla__button embla__button--prev'
            >
              {prevArrow || ''}
            </button>
          </div>
        )
      }

      <div className='embla'>
        {
          showHeader && (
            <div className={`game-slider-container-heading-content ${showHeader ? 'pb-2' : ''}`}>
              <div className='d-flex align-items-center gap-2'>
                <>{headerIcon()}</>
                <p className='heading-text m-0'>
                  {headerTitle}
                </p>
              </div>
              <div className='d-flex align-items-center gap-2 header-button-wrapper'>
                {showViewAllButton && (
                  <div className='view-all-btn-wrapper'>
                    <Button
                      variant={BUTTON_VARIANT.OUTLINE}
                      id='view-all-button'
                      className='view-all-btn'
                      onClick={onClickViewAll}
                    >
                      {viewAllButtonText}
                    </Button>
                  </div>
                )}
                {showNavigation && controlPosition === CONTROL_POSITION.TOP && (
                  <div
                    className='embla__controls embla-next-prev-button-wrapper'
                    id='embla-next-prev-button-wrapper'
                  >
                    <button
                      onClick={scrollPrev}
                      className={`embla__button ${nextArrow ? 'custom-slider-button' : 'embla__button--prev'}`}
                    >
                      {prevArrow || ''}
                    </button>
                    <button
                      onClick={scrollNext}
                      className={`embla__button ${nextArrow ? 'custom-slider-button' : 'embla__button--next'}`}
                    >
                      {nextArrow || ''}
                    </button>
                  </div>
                )}
              </div>
            </div>)
        }

        <div className='embla__viewport' ref={emblaRef}>
          <div className='embla__container'>
            {slides.map((slide, index) => {
              // Calculate gap value for margin application
              const gapValue = gameSliderCardGap && type === SLIDE_TYPES.GAME_CARD
                ? `${gameSliderCardGap}px`
                : null

              // Apply margin for gap
              // When loop is enabled, apply margin to all slides including last one
              // When loop is disabled, skip margin on last slide to avoid extra space
              const gapMargin = gapValue && (loop || index < slides.length - 1)
                ? (isVertical ? { marginBottom: gapValue } : { marginRight: gapValue })
                : {}

              // Calculate slide styles
              const slideStyles = !!gameSliderDragFreeCardWidth && type === SLIDE_TYPES.GAME_CARD
                ? {
                    ...(gameSliderDragFreeCardWidth
                      ? { flex: `0 0 ${gameSliderDragFreeCardWidth}`, width: gameSliderDragFreeCardWidth }
                      : {}),
                    ...gapMargin
                  }
                : {
                    flex: `0 0 ${slideWidth}`,
                    width: slideWidth,
                    ...gapMargin
                  }

              return (
                <div
                  className='embla__slide'
                  key={index}
                  style={slideStyles}
                >
                  {sliderCard({ index, game: slide, cardProps })}
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {showNavigation && controlPosition === CONTROL_POSITION.BOTTOM && (
        <div className='embla__controls embla_wrapper_bottom_controls'>
          <button
            onClick={scrollPrev}
            className={`embla__button ${prevArrow ? 'custom-slider-button' : 'embla__button--prev'}`}
          >
            {prevArrow || ''}
          </button>
          {renderNavigation()}
          <button
            onClick={scrollNext}
            className={`embla__button ${nextArrow ? 'custom-slider-button' : 'embla__button--next'}`}
          >
            {nextArrow || ''}
          </button>
        </div>
      )}

      {
        showNavigation && controlPosition === CONTROL_POSITION.LEFT_RIGHT && (
          <div className='embla__controls_left_right'>
            <button
              disabled={nextDisabled}
              onClick={scrollNext}
              className='embla__button embla__button--next'
            >
              {nextArrow || ''}
            </button>
          </div>
        )
      }
    </div>
  )
}

export default SliderDefault
